import { z } from "zod";

/**
 * Auth validation schemas (single source of truth).
 *
 * Validation rules are intentionally strict on the frontend so users get fast
 * feedback; the backend will re-validate independently in a later step.
 */

const passwordRule = z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(64, "Password is too long")
    .regex(/[A-Z]/, "Add at least one uppercase letter")
    .regex(/[a-z]/, "Add at least one lowercase letter")
    .regex(/[0-9]/, "Add at least one number");

const phoneRule = z
    .string()
    .trim()
    .min(1, "Phone number is required")
    .regex(/^\+?\d[\d\s-]{7,14}$/, "Enter a valid phone number");

export const loginSchema = z.object({
    email: z
        .string()
        .trim()
        .min(1, "Email is required")
        .email("Enter a valid email"),
    password: z.string().min(1, "Password is required"),
    remember: z.boolean().optional(),
});

export type LoginValues = z.infer<typeof loginSchema>;

export const signupSchema = z
    .object({
        fullName: z
            .string()
            .trim()
            .min(2, "Please enter your full name")
            .max(60, "Name is too long"),
        phone: phoneRule,
        email: z
            .string()
            .trim()
            .min(1, "Email is required")
            .email("Enter a valid email"),
        password: passwordRule,
        confirmPassword: z.string().min(1, "Please confirm your password"),
        acceptTerms: z
            .boolean()
            .refine((v) => v === true, "You must accept the terms to continue"),
    })
    .refine((data) => data.password === data.confirmPassword, {
        path: ["confirmPassword"],
        message: "Passwords do not match",
    });

export type SignupValues = z.infer<typeof signupSchema>;
